import {useSessionCookie} from "../../hooks/useSessionCookie";
import {
    Accordion,
    AccordionDetails,
    AccordionGroup,
    AccordionSummary,
    Box,
    Button,
    Drawer,
    IconButton,
    Option,
    Select,
    Stack,
    Typography
} from "@mui/joy"
import React, {useState, useRef, useEffect} from "react"
import {Link, useNavigate, useLocation} from "react-router-dom"
import {
    BookOpenCheck,
    BookOpenText,
    ChevronDown,
    Icon,
    Languages,
    LayoutDashboardIcon,
    Menu,
    MenuIcon
} from "lucide-react"
import {useUITheme} from "../../hooks/useUITheme"
import {APP_URL} from "../../constants";
import {appHost, appRoute, guestRoute, productionMode} from "../../utils/routes";
import {useTranslation} from "react-i18next";
import Logo from "../Logo";
import {useLanguage} from "../../hooks/useLanguage";

// Shown in each language's own name so a visitor can find theirs whatever the page is in.
const LANGUAGE_OPTIONS = [
    {value: "en", short: "EN", label: "English"},
    {value: "fr", short: "FR", label: "Français"},
]

const LanguageSwitch = (props: { display: { xs: string, md: string } }) => {
    const language = useLanguage()
    const {t} = useTranslation()
    return <Select
        size={"sm"}
        variant={"plain"}
        color={"neutral"}
        aria-label={t("languageSwitchLabel")}
        value={language.lang}
        onChange={(_, value) => value && language.setLanguage(value)}
        startDecorator={<Languages size={18}/>}
        renderValue={option => LANGUAGE_OPTIONS.find(value => value.value === option?.value)?.short}
        sx={{display: props.display, alignSelf: "center", flexShrink: 0}}>
        {LANGUAGE_OPTIONS.map(option =>
            <Option key={option.value} value={option.value}>{option.label}</Option>)}
    </Select>
}

const EXAM_LINKS = [
    {slug: "ielts", label: "IELTS"},
    {slug: "toefl", label: "TOEFL"},
    {slug: "tcf", label: "TCF"},
    {slug: "tef", label: "TEF"},
    {slug: "delf-dalf", label: "DELF & DALF"},
]

type GuestLinkProps = { to: string, style?: React.CSSProperties, onClick?: () => void, children?: React.ReactNode }

/**
 * Guest pages (pricing, exams…) only exist on the main domain. On the app subdomain — where a
 * signed-out visitor can still land, e.g. on the 404 page — a router link to them would 404 again,
 * so there the links go to the main site instead.
 */
const GuestLink = ({to, ...props}: GuestLinkProps) =>
    appHost ? <a href={guestRoute(to)} {...props}/> : <Link to={to} {...props}/>

export type GuestNavbarActive = "login" | "register" | "pricing" | "about" | "contact" | null

const GuestNavbar = ({active = null}: { active?: GuestNavbarActive }) => {
    const {userSession} = useSessionCookie()
    const uiTheme = useUITheme()
    const navigate = useNavigate()
    const location = useLocation()
    const {t} = useTranslation()
    const [examsOpen, setExamsOpen] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const examsRef = useRef<HTMLDivElement>(null)
    const isExamPage = location.pathname.startsWith("/Exams/")

    const handleStartPreparing = () => {
        if (productionMode)
            window.location.href = APP_URL
        else navigate("/register")
    }

    const handleGoToDashboard = () => {
        if (productionMode)
            window.location.href = APP_URL
        else navigate(appRoute())
    }

    const handleLogin = () => {
        if (productionMode)
            window.location.href = APP_URL + "/login"
        else navigate("/login")
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (examsRef.current && !examsRef.current.contains(e.target as Node)) {
                setExamsOpen(false)
            }
        }
        if (examsOpen) {
            document.addEventListener("mousedown", handleClickOutside)
        }
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [examsOpen])

    return (
        <Stack
            direction={{md: "row"}}
            justifyContent={{xs: "flex-start", sm: "space-between"}}
            paddingX={{xs: 2, sm: 4, lg: 6}}
            paddingY={2}
            sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "background.surface",
            }}>
            <Stack justifyContent={"flex-start"} direction={"row"}>
                <IconButton onClick={() => setDrawerOpen(true)} size={"lg"} sx={{display: {md: "none"}}}>
                    <MenuIcon/>
                </IconButton>

                <Box
                    component="img"
                    width={{xs: "10rem", sm: "12rem"}}
                    height="auto"
                    src={uiTheme.fullLogo}
                    alt="LinguaScore"
                    sx={{cursor: "pointer"}}
                    onClick={() => navigate("/")}
                />

                <Box flex={1} display={{md: "none"}}/>
                <LanguageSwitch display={{xs: "flex", md: "none"}}/>
            </Stack>

            <Stack
                direction="row"
                flex={1}
                justifyContent="center"
                alignItems="center"
                gap={3}
                sx={{flexWrap: "wrap", display: {md: "flex", xs: "none"}}}>
                <Box ref={examsRef} sx={{position: "relative", display: "inline-block"}}>
                    <Box
                        onClick={() => setExamsOpen(!examsOpen)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            color: isExamPage ? "var(--joy-palette-primary-500)" : "var(--joy-palette-text-primary)",
                            fontWeight: isExamPage ? 600 : 400,
                        }}>
                        <Typography level="body-md">{t("examLabel")}</Typography>
                        <ChevronDown
                            size={18}
                            style={{
                                transform: examsOpen ? "rotate(180deg)" : "none",
                                transition: "transform 0.2s",
                            }}
                        />
                    </Box>
                    {examsOpen && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: "100%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                marginTop: 1,
                                minWidth: 160,
                                padding: 0.5,
                                borderRadius: "sm",
                                bgcolor: "background.surface",
                                boxShadow: "md",
                                border: "1px solid",
                                borderColor: "divider",
                                zIndex: 1100,
                            }}>
                            {EXAM_LINKS.map(({slug, label}) => (
                                <GuestLink
                                    key={slug}
                                    to={`/exams/${slug}`}
                                    style={{textDecoration: "none"}}
                                    onClick={() => setExamsOpen(false)}>
                                    <Box
                                        sx={{
                                            padding: "8px 12px",
                                            borderRadius: "xs",
                                            "&:hover": {
                                                bgcolor: "background.level1",
                                            }
                                        }}>
                                        <Typography level="body-sm">{label}</Typography>
                                    </Box>
                                </GuestLink>
                            ))}
                        </Box>
                    )}
                </Box>
                <GuestLink to="/pricing" style={{textDecoration: "none"}}>
                    <Typography
                        level="body-md"
                        sx={{
                            color: active === "pricing" ? "primary.500" : "text.primary",
                            fontWeight: active === "pricing" ? 600 : 400,
                        }}>
                        {t("pricingLabel")}
                    </Typography>
                </GuestLink>
                <GuestLink to="/about" style={{textDecoration: "none"}}>
                    <Typography
                        level="body-md"
                        sx={{
                            color: active === "about" ? "primary.500" : "text.primary",
                            fontWeight: active === "about" ? 600 : 400,
                        }}>
                        {t("aboutLabel")}
                    </Typography>
                </GuestLink>
                <GuestLink to="/contact" style={{textDecoration: "none"}}>
                    <Typography
                        level="body-md"
                        sx={{
                            color: active === "contact" ? "primary.500" : "text.primary",
                            fontWeight: active === "contact" ? 600 : 400,
                        }}>
                        {t("contactLabel")}
                    </Typography>
                </GuestLink>
            </Stack>
            <Stack direction={"row"}
                   alignSelf={"center"}
                   width={{xs: "100%", md: "max-content"}}
                   gap={2}>

                <LanguageSwitch display={{xs: "none", md: "flex"}}/>

                {userSession ?
                    <Button size={"lg"} sx={{width: "100%"}} variant={"soft"} color={"neutral"}
                            startDecorator={<LayoutDashboardIcon/>} onClick={handleGoToDashboard}>
                        {t("goToDashboard")}
                    </Button> :
                    <>
                        <Button size={"lg"} sx={{width: {xs: "50%", md: "max-content"}}}
                                variant={active === "login" ? "soft" : "soft"}
                                color={active === "login" ? "primary" : "neutral"}
                                onClick={handleLogin}>
                            {t("loginActionLabel")}
                        </Button>
                        <Button
                            variant={active === "register" ? "solid" : "solid"}
                            onClick={handleStartPreparing}
                            sx={{
                                width: {xs: "50%", md: "max-content"}
                            }}
                            size={"lg"}>
                            {t("registerActionLabel")}
                        </Button>
                    </>}
            </Stack>


            <Drawer
                anchor={"top"}
                open={drawerOpen}
                size={"sm"}
                onClose={() => setDrawerOpen(false)}>

                <Stack overflow={"hidden"} boxSizing={"border-box"} paddingTop={3} paddingBottom={3} paddingX={4}
                       gap={2}
                       justifyContent={"space-between"}>

                    <AccordionGroup>
                        <Accordion>
                            <AccordionSummary sx={{paddingLeft: 0}}>
                                <Typography level="body-md">{t("examLabel")}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack>
                                    {EXAM_LINKS.map(({slug, label}) => (
                                        <GuestLink
                                            key={slug}
                                            to={`/exams/${slug}`}
                                            style={{textDecoration: "none"}}
                                            onClick={() => setExamsOpen(false)}>
                                            <Box
                                                sx={{
                                                    padding: "8px 12px",
                                                    borderRadius: "xs",
                                                    "&:hover": {
                                                        bgcolor: "background.level1",
                                                    },
                                                }}>
                                                <Typography level="title-lg">{label}</Typography>
                                            </Box>
                                        </GuestLink>
                                    ))}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    </AccordionGroup>


                    <GuestLink to="/pricing" style={{textDecoration: "none"}}>
                        <Typography
                            level="title-lg"
                            sx={{
                                color: active === "pricing" ? "primary.500" : "text.primary",
                                fontWeight: active === "pricing" ? 600 : 400,
                            }}>
                            {t("pricingLabel")}
                        </Typography>
                    </GuestLink>
                    <GuestLink to="/about" style={{textDecoration: "none"}}>
                        <Typography
                            level="title-lg"
                            sx={{
                                color: active === "about" ? "primary.500" : "text.primary",
                                fontWeight: active === "about" ? 600 : 400,
                            }}>
                            {t("aboutLabel")}
                        </Typography>
                    </GuestLink>
                    <GuestLink to="/contact" style={{textDecoration: "none"}}>
                        <Typography
                            level="title-lg"
                            sx={{
                                color: active === "contact" ? "primary.500" : "text.primary",
                                fontWeight: active === "contact" ? 600 : 400,
                            }}>
                            {t("contactLabel")}
                        </Typography>
                    </GuestLink>

                </Stack>
            </Drawer>
        </Stack>
    )
}

export default GuestNavbar
